import { createApi } from '@reduxjs/toolkit/query/react';
import { Endpoint } from 'services/endpoints';

import { HTTPMethod, Response } from 'types/http';

import { fetchBaseQueryWithAuth } from '../api';
import { WebhookDestinationRef, planWebhookSync } from './planWebhookSync';

const REDUCER_PATH = 'eventDestinationsApi';

const TAGS = {
  EVENT_DESTINATIONS: 'eventDestinations',
};

type EventDestinationList = {
  data: WebhookDestinationRef[];
};

export const eventDestinationsApi = createApi({
  reducerPath: REDUCER_PATH,
  baseQuery: fetchBaseQueryWithAuth,
  tagTypes: [TAGS.EVENT_DESTINATIONS],
  endpoints: (builder) => ({
    listEventDestinations: builder.query<EventDestinationList, string>({
      query: (accountUuid) => ({
        url: Endpoint.EventDestinations.List(accountUuid),
        method: HTTPMethod.POST,
        body: {},
      }),
      transformResponse: (response: Response<EventDestinationList>) => response.data ?? { data: [] },
      providesTags: [TAGS.EVENT_DESTINATIONS],
    }),
    syncWebhookDestination: builder.mutation<boolean, { accountUuid: string; previousField?: string | null; nextField?: string | null }>({
      async queryFn({ accountUuid, previousField, nextField }, _api, _extra, baseQuery) {
        const listed = await baseQuery({
          url: Endpoint.EventDestinations.List(accountUuid),
          method: HTTPMethod.POST,
          body: {},
        });
        if (listed.error) {
          return { error: listed.error };
        }

        const page = (listed.data as Response<EventDestinationList>).data;
        const plan = planWebhookSync({
          destinations: page?.data ?? [],
          previousField,
          nextField,
        });

        if (plan.type === 'create' || plan.type === 'replace') {
          const created = await baseQuery({
            url: Endpoint.EventDestinations.Create(accountUuid),
            method: HTTPMethod.POST,
            body: {
              destination: plan.destination,
              eventCategories: plan.eventCategories,
              ...(plan.name ? { name: plan.name } : {}),
              ...(plan.enabled !== undefined ? { enabled: plan.enabled } : {}),
            },
          });
          if (created.error) {
            return { error: created.error };
          }
        }

        if (plan.type === 'delete' || plan.type === 'replace') {
          const deleted = await baseQuery({
            url: Endpoint.EventDestinations.ById(accountUuid, plan.uuid),
            method: HTTPMethod.DELETE,
          });
          if (deleted.error) {
            return { error: deleted.error };
          }
        }

        return { data: true };
      },
      invalidatesTags: (result, error) => (error ? [] : [TAGS.EVENT_DESTINATIONS]),
    }),
  }),
});

export const { useListEventDestinationsQuery, useSyncWebhookDestinationMutation } = eventDestinationsApi;

export default eventDestinationsApi;
